import { useEffect, useMemo } from 'react';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { useStylesheets } from 'hooks/useStylesheets';
import { usePopupKeys } from 'hooks/usePopupKeys';
import { useCurrentUser, usePermissions, getBootUserId } from 'hooks/useDashboardData';
import { useProjectSocket, useUnreadProjectsSync } from 'hooks/useProjectSocket';
import { useAppDispatch } from 'app/hooks';
import { setSortFromUserOrder, setManageColumns } from 'app/slices/dashboardSlice';
import type { IDashboardUser, BoardPermission, IProjectBoardTab } from 'models';
import GlobalHeader from './components/GlobalHeader';
import DashboardShell from './components/DashboardShell';
import PageFooter from './components/PageFooter';

// Whiteboard CSS loaded scoped to this route (§8). Order: skeleton first, then
// styles (styles.css wins cascade ties). react.html already loads common.css +
// header.css globally; these two are injected after them in <head> so the
// documented cascade (styles.css last) holds.
const WHITEBOARD_CSS = [
  '/static/css/whiteboard/skeleton.css',
  '/static/css/whiteboard/styles.css',
];

/** Resolve the board permission for the 'project' tab (§ permission gating). */
const resolvePermission = (
  tabs: IProjectBoardTab[] | undefined
): BoardPermission => {
  const tab = tabs?.find((t) => t.tab === 'project');
  if (!tab) return 'no_access';
  return tab.access ? 'access' : 'view';
};

/**
 * Dashboard ("whiteboard") page — sub-phase 2a FOUNDATION + SHELL.
 *
 * - title "Dashboard"; sets body[data-user_id].
 * - renders the global popup mount #wrapper-div.popup.popup-wrapper.
 * - loads whiteboard CSS scoped to this route (removed on unmount so it doesn't
 *   bleed onto auth pages).
 * - renders GlobalHeader + DashboardShell + PageFooter.
 * - role gating: no role_id ⇒ NoRolePanel (in the shell) + header `non-clickable`
 *   + footer full-width/footer-margin.
 *
 * Routed at "" and "/" (App.tsx). The project table body, modal internals and
 * realtime are built in later sub-phases.
 */
const DashboardPage = () => {
  const dispatch = useAppDispatch();
  useDocumentTitle('Dashboard');
  // Gate first paint on the route CSS (skeleton/styles) loading — the sheets
  // are injected AFTER React mounts, and react.html only ships common.css +
  // header.css in <head>. Without the gate the header popups, task rail and
  // table paint as raw unstyled text in the load gap (QA batch 3 #3 — same
  // FOUC class fixed on ProjectBoardPage.tsx, see its cssLoaded gate). Legacy
  // whiteboard.html loads all CSS render-blocking in <head>; holding a brief
  // blank frame here mirrors that (parity).
  const cssLoaded = useStylesheets(WHITEBOARD_CSS);
  // Global popup keyboard handling (Esc → Cancel + dismiss; Enter → autofocus
  // button), §3 shared modal convention / parity 126.
  usePopupKeys();

  // Realtime (§7): ONE Socket.IO `'data'` listener wiring project syncdata to
  // the RTK Query cache (post→refetch list, delete→remove, put→refetch single,
  // update_project_notification_data→10s-debounced refetch). Connects once and
  // fails gracefully when no socket server is reachable (e.g. local Vite dev;
  // dev3 HAS one — verified connected 2026-07-07).
  useProjectSocket();
  // Populate unreadProjectIds from unread `new_project` notifications → drives
  // .unread-notification on rows (cleared on click, wired in 2c).
  useUnreadProjectsSync();

  const { data: user } = useCurrentUser();
  const { data: permData } = usePermissions();

  const permission = useMemo(
    () => resolvePermission(permData?.permissions?.project_board_tabs),
    [permData]
  );

  const dashUser = (user ?? null) as IDashboardUser | null;
  const noRole = !!user && !dashUser?.role_id;

  // body[data-user_id] = logged-in user id (server-rendered shell parity).
  useEffect(() => {
    const id = dashUser?.id ?? getBootUserId();
    if (id != null) document.body.setAttribute('data-user_id', String(id));
    return () => {
      document.body.removeAttribute('data-user_id');
    };
  }, [dashUser?.id]);

  // Seed sort + manage-columns UI state from the user prefs once loaded.
  useEffect(() => {
    if (!dashUser) return;
    dispatch(setSortFromUserOrder(dashUser.dashboard_projects_default_order));
    if (typeof dashUser.manage_columns === 'string' && dashUser.manage_columns.length) {
      const ids = Array.from(
        new Set(
          dashUser.manage_columns
            .split(',')
            .map((s) => parseInt(s, 10))
            .filter((n) => !Number.isNaN(n))
        )
      );
      dispatch(setManageColumns(ids));
    }
  }, [dispatch, dashUser]);

  // Hold a blank frame until the route stylesheets have applied (see the
  // useStylesheets gate above). All hooks run first, so this only affects
  // paint; effects (body data-user_id, sort seeding, socket) still run.
  if (!cssLoaded) return null;

  return (
    <>
      {/* Global empty popup mount (server shell parity). */}
      <div id="wrapper-div" className="popup popup-wrapper" />

      {/* No-role: #ksdm-header gets `non-clickable` (§ role gating). */}
      <GlobalHeader noRole={noRole} />

      <DashboardShell user={dashUser} permission={permission} />

      <PageFooter noRole={noRole} />
    </>
  );
};

export default DashboardPage;
